import { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from "@mui/material";
import { Controller, useWatch } from "react-hook-form";
import { Add as AddIcon } from "@mui/icons-material";
import RoundIconCard from "../../../mini-components/RoundIconCard";
import defaultAmenities, { customIcons } from "../amenitiesList";

const AmenitiesSection = ({ control }) => {
  const [amenities, setAmenities] = useState(defaultAmenities);
  const [open, setOpen] = useState(false);
  const [newLabel, setNewLabel] = useState("");

  // 👀 watch form field to get backend values
  const selectedAmenities = useWatch({
    control,
    name: "amenities",
    defaultValue: [],
  });

  useEffect(() => {
    if (!selectedAmenities) return;

    selectedAmenities.forEach((id) => {
      if (!amenities.some((a) => a.id === id)) {
        setAmenities((prev) => [
          ...prev,
          {
            id,
            label: id, // fallback label, can be prettified
            icon: customIcons[Math.floor(Math.random() * customIcons.length)],
          },
        ]);
      }
    });
  }, [selectedAmenities, amenities]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setNewLabel("");
  };

  const handleSaveAmenity = () => {
    const label = newLabel.trim();
    if (!label) return;

    const id = label.toLowerCase().replace(/\s+/g, "-");
    if (amenities.some((a) => a.id === id)) {
      return; // prevent duplicate
    }

    const randomIcon = customIcons[Math.floor(Math.random() * customIcons.length)];

    const newAmenity = { id, label, icon: randomIcon };
    setAmenities((prev) => [...prev, newAmenity]);
    handleClose();
  };

  return (
    <Grid item xs={12}>
      <Typography variant="subtitle1" gutterBottom sx={{ color: "#fff" }}>
        Amenities
      </Typography>
      <Controller
        name="amenities"
        control={control}
        render={({ field }) => (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: { xs: 1, sm: 2, md: 3 },
              justifyContent: { xs: "center", sm: "flex-start" },
            }}
          >
            {amenities.map((amenity) => {
              const isSelected = field.value.includes(amenity.id);
              return (
                <RoundIconCard
                  key={amenity.id}
                  icon={amenity.icon}
                  label={amenity.label}
                  selected={isSelected}
                  onClick={() => {
                    const updated = isSelected
                      ? field.value.filter((id) => id !== amenity.id)
                      : [...field.value, amenity.id];
                    field.onChange(updated);
                  }}
                />
              );
            })}

            {/* Add Amenity Card */}
            <RoundIconCard
              icon={<AddIcon fontSize="large" />}
              label="Add Amenity"
              selected={false}
              onClick={handleOpen}
              sx={{ border: "2px dashed #ccc" }}
            />
          </Box>
        )}
      />

      {/* Dialog for adding custom amenity */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle>Add a custom amenity</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Amenity name"
            type="text"
            fullWidth
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="e.g., EV Charger, Beach Access"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveAmenity}
            disabled={!newLabel.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default AmenitiesSection;
